import { t } from "@lingui/macro";
import { useTheme } from "@shopify/restyle";
import { useSetAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { useWindowDimensions } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CheckBox } from "@whoosh/design-system/src/components/controls/CheckBox/CheckBox";
import {
  Avatar,
  getAvatarTypeFromPatronType,
} from "@whoosh/design-system/src/components/display";
import { PlusIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { AddPeopleSearch_Atom } from "../atoms/stores";
import { Patron } from "../types";

type PatronsListItemProps = {
  item: Patron;
  checkNewDisabled?: boolean;
  initiallyChecked?: boolean;
  handleCheck: (player: Patron, checked: boolean) => void;
  onRemovePlayer?: (playerId: string) => void;
  hideOverride?: boolean;
  keepItemInListAfterCheck?: boolean;
  displayName?: string;
  description?: string;
  selectionAction?: "swap" | "replace";
  selectionAccessibilityLabel?: string;
  testID?: string;
};

const UnmemoizedPatronsListItem = ({
  item,
  checkNewDisabled,
  initiallyChecked,
  handleCheck,
  onRemovePlayer,
  hideOverride,
  keepItemInListAfterCheck = false,
  displayName,
  description,
  selectionAction,
  selectionAccessibilityLabel,
  testID,
}: PatronsListItemProps) => {
  const setSearch = useSetAtom(AddPeopleSearch_Atom);
  const [checked, setChecked] = useState(initiallyChecked);

  useEffect(() => {
    setChecked(initiallyChecked);
  }, [initiallyChecked]);

  const name =
    displayName ?? `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();

  const isNotEligible = item.isNotEligible;

  const hideComponent =
    hideOverride !== undefined
      ? hideOverride
      : !keepItemInListAfterCheck && checked && !initiallyChecked;

  const { width } = useWindowDimensions();
  const theme = useTheme();
  const isMobileLayout = width < theme.breakpoints.lg;

  const onCheck = (newCheck: boolean) => {
    if (!checkNewDisabled) {
      setChecked(newCheck);
      if (
        keepItemInListAfterCheck &&
        !newCheck &&
        onRemovePlayer &&
        item.playerId
      ) {
        onRemovePlayer(item.playerId);
      } else {
        handleCheck(item, newCheck);
      }
      // Only clear search if we're hiding the item
      if (!keepItemInListAfterCheck) {
        setSearch("");
      }
    }
  };

  const usesCheckboxLayout =
    !selectionAction && (isMobileLayout || keepItemInListAfterCheck);

  const rowContent = (
    <HStack
      px={6}
      style={{ borderRadius: 8 }}
      py={2}
      my={2}
      justifyContent="space-between"
      alignItems="center"
    >
      <HStack alignItems="center" gap={4}>
        <Box opacity={isNotEligible ? 0.7 : 1}>
          <Avatar
            profileImagePublicId={item.profileImageId || ""}
            firstName={item.firstName}
            lastName={item.lastName}
            size={"sm"}
            type={
              isNotEligible
                ? "secondary"
                : getAvatarTypeFromPatronType(item.type)
            }
          />
        </Box>
        <VStack gap={1}>
          <HStack alignItems={"center"} gap={1}>
            <Text
              variant={"mainBaseMedium"}
              color={isNotEligible ? "text-secondary" : "text-primary"}
            >
              {name}
            </Text>
            <Text
              color={isNotEligible ? "text-secondary" : "text-primary"}
              variant="mainMicro"
            >
              {isNotEligible ? t`(Ineligible)` : null}
            </Text>
          </HStack>
          {description ? (
            <Text variant="mainTiny" color="text-secondary">
              {description}
            </Text>
          ) : null}
        </VStack>
      </HStack>
      {isNotEligible ? null : (
        <>
          {usesCheckboxLayout ? (
            // Purely visual indicator — no onPress so isInteractive=false and
            // CheckBox renders a plain Box with no role/tabindex/aria attrs.
            // The outer Pressable carries all AT semantics and handles taps.
            <CheckBox
              isChecked={checked}
              testID={`add-player-btn-${item.firstName}-${item.lastName}-${
                item.internalIdentifier || ""
              }`}
            />
          ) : (
            // Action/desktop: Button is the sole interactive element; the outer
            // Pressable is inert to AT (focusable={false}, role="none").
            <Button
              accessibilityLabel={
                selectionAction
                  ? selectionAccessibilityLabel ??
                    (selectionAction === "replace"
                      ? t`Replace with ${name}`
                      : t`Swap with ${name}`)
                  : checked
                  ? t`Remove ${name}`
                  : t`Add ${name}`
              }
              variant="secondary"
              onPress={() => onCheck(!checked)}
              isDisabled={checkNewDisabled}
              size="tiny"
              testID={`add-player-btn-${item.firstName}-${item.lastName}-${
                item.internalIdentifier || ""
              }`}
              leftIcon={selectionAction ? undefined : <PlusIcon />}
            >
              {selectionAction === "replace"
                ? t`Replace`
                : selectionAction
                ? t`Swap`
                : null}
            </Button>
          )}
        </>
      )}
    </HStack>
  );

  return hideComponent ? null : (
    <Pressable
      disabled={isNotEligible || (checkNewDisabled && !checked)}
      onPress={() => onCheck(!checked)}
      opacity={!checked && checkNewDisabled ? 0.5 : 1}
      testID={
        testID ??
        `add-player-row-${item.firstName}-${item.lastName}-${
          item.internalIdentifier || ""
        }`
      }
      // Mobile/always-show: the row IS the checkbox for screen readers.
      // Desktop: inert to AT — the inner Button is the only interactive element.
      {...(usesCheckboxLayout
        ? {
            accessibilityRole: "checkbox" as const,
            accessibilityState: {
              checked: !!checked,
              disabled: !!isNotEligible || (!checked && !!checkNewDisabled),
            },
            "aria-checked": !!checked,
            accessibilityLabel: checked
              ? t`Remove ${name} from booking`
              : t`Add ${name} to booking`,
          }
        : {
            accessibilityRole: "none" as const,
            focusable: false,
          })}
    >
      {rowContent}
    </Pressable>
  );
};

export const PatronsListItem = React.memo(UnmemoizedPatronsListItem);
